import React from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Users, Zap, ArrowRight, Check } from 'lucide-react';
import { TeamArt } from './illustrations';
import useInView from '../hooks/useInView';

const REASONS = [
  {
    icon: Zap,
    accent: 'accent-3',
    title: 'Shape the product',
    description:
      'Beta users get direct access to our team. Your workflows, your pain points and your feedback go into the roadmap before we launch publicly.',
  },
  {
    icon: Users,
    accent: 'accent-4',
    title: 'Free for a full month',
    description:
      'Full platform access for up to 10 agents, at zero cost for the beta period. Every module is unlocked — the only limits are the plan ceilings, not feature gates.',
  },
  {
    icon: Megaphone,
    accent: 'accent-5',
    title: 'Be first to market',
    description:
      'Get your call center running on WASLX before your competitors do. Early adopters lock in the best rates when paid plans launch.',
  },
];

/**
 * The early-access panel.
 *
 * Every other section on this page is a GRID of separate objects — the KPI
 * pairs, the six feature cards, the numbered step rail, the tab list and its
 * detail. This one is deliberately a single panel instead: one bounded card
 * with the artwork bleeding off its left edge and the three reasons stacked
 * inside it on hairlines, rather than three more cards floating on the page.
 * That is what makes it read as its own thing rather than as another deck.
 *
 * Built only from primitives that already exist (.card--gradient, .icon-tile,
 * .chip, border-line) so it needs no new stylesheet rule.
 */
const TestimonialsSection = () => {
  const [ref, inView] = useInView();

  return (
    <section ref={ref} className={`section section--surface reveal ${inView ? 'is-inview' : ''}`}>
      <div className="container-page">
        <div className="section-head">
          <span className="eyebrow accent-3">Early adopters</span>
          <h2 className="section-title">
            Be among the <span className="display-accent">first</span>
          </h2>
          <p className="section-sub">
            We are just getting started. Join the beta, help us build the best mobile call center
            platform on the market, and take a full month free.
          </p>
        </div>

        <div className="accent-3 card card--gradient reveal-color overflow-hidden !p-0">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr]">
            {/* Artwork bleeds to the panel's own edges — no inner padding, no
                second card around it. It is part of the panel, not a tile
                sitting on one. */}
            <div className="relative flex items-center justify-center bg-well/40 p-8 lg:p-10">
              <span className="shape shape--spark shape--sm shape--tl shape--rot15" aria-hidden="true" />
              <TeamArt className="h-auto w-full max-w-sm" />
            </div>

            {/* The reasons live inside the panel, separated by hairlines
                rather than by gaps — a list, not a grid. */}
            <div className="on-art-invert border-t border-line p-8 lg:border-l lg:border-t-0 lg:p-10">
              <span className="chip">Beta · 1 month free</span>

              <div className="mt-6 divide-y divide-line">
                {REASONS.map(reason => {
                  const Icon = reason.icon;
                  return (
                    <div key={reason.title} className={`${reason.accent} flex items-start gap-4 py-5 first:pt-0 last:pb-0`}>
                      <span className="icon-tile icon-tile--sm mt-0.5 flex-shrink-0">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-bold text-content">{reason.title}</h3>
                        <p className="mt-1 text-[0.9375rem] leading-relaxed text-content-muted">
                          {reason.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 border-t border-line pt-6">
                <Link to="/contact" className="btn-pill btn-pill--white">
                  Join the beta
                  <ArrowRight className="h-[18px] w-[18px]" />
                </Link>

                <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                  {['No credit card', '1 month free', 'Cancel anytime'].map(item => (
                    <li key={item} className="flex items-center gap-1.5 text-sm text-content-muted">
                      <Check className="h-3.5 w-3.5 flex-shrink-0 text-white" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
